import { useEffect, useMemo, useState } from "react";
import type { EvidenceItem } from "../../../domain/contracts";
import { useProtectedMedia } from "../../../hooks/useProtectedMedia";
import { buildMediaPreviewItems } from "../../../services/viewModels";

type UseVillageEvidenceGalleryArgs = {
  evidenceItems: EvidenceItem[];
  isEvidenceLoading: boolean;
  resetKey?: string | null;
  enabled?: boolean;
  limit?: number;
};

export function useVillageEvidenceGallery({
  evidenceItems,
  isEvidenceLoading,
  resetKey = null,
  enabled = true,
  limit = 6,
}: UseVillageEvidenceGalleryArgs) {
  const [isExpanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(false);
  }, [resetKey]);

  const mediaObjectUrls = useProtectedMedia(evidenceItems, {
    enabled: enabled && isExpanded,
    limit,
  });

  const mediaPreviews = useMemo(
    () => buildMediaPreviewItems(evidenceItems, mediaObjectUrls, limit),
    [evidenceItems, mediaObjectUrls, limit],
  );

  const hasImagePreview = mediaPreviews.some((item) => item.mediaType === "image");
  const hasLoadedImage = mediaPreviews.some((item) => item.mediaType === "image" && Boolean(item.objectUrl));

  const isLoading = isExpanded && (isEvidenceLoading || (hasImagePreview && !hasLoadedImage));

  return {
    isExpanded,
    mediaPreviews,
    isLoading,
    toggleExpanded() {
      setExpanded((current) => !current);
    },
  };
}
